import { QuizState } from "@/models/QuizState";
import { AnsweredQuestion, Question, Quiz } from "@puls/puls-common-constants";
import { skillAssessmentStatusesUtils, skillAssessmentUtils } from "@puls/puls-general-utils";
export const QuizGetters = {
	IS_QUIZ_LOADED: 'IS_QUIZ_LOADED',
	IS_QUIZ_ATTEMPT_FAILED: 'IS_QUIZ_ATTEMPT_FAILED',
	IS_QUIZ_PASSED: 'IS_QUIZ_PASSED',
	IS_FIRST_TIME_QUIZ_NOT_STARTED: 'IS_FIRST_TIME_QUIZ_NOT_STARTED',
	IS_HAVE_RETRY: 'IS_HAVE_RETRY',
	IS_QUIZ_IN_PROGRESS: 'IS_QUIZ_IN_PROGRESS',
	GET_CURRENT_QUESTION: 'GET_CURRENT_QUESTION',
	GET_QUESTION_BY_UUID: 'GET_QUESTION_BY_UUID',
	GET_QUIZ_NAME: 'GET_QUIZ_NAME',
	GET_QUESTIONS_COUNT: 'GET_QUESTIONS_COUNT',
	GET_TIME_LEFT: 'GET_TIME_LEFT',
	GET_QUIZ_IMAGE: 'GET_QUIZ_IMAGE',
	GET_QUIZ_TIME: 'GET_QUIZ_TIME',
	GET_QUIZ_DESCRIPTION: 'GET_QUIZ_DESCRIPTION',
	GET_QUIZ_PASSING_SCORE: 'GET_QUIZ_PASSING_SCORE',
	GET_CORRECT_ANSWERS_COUNT: 'GET_CORRECT_ANSWERS_COUNT',
	GET_CURRENT_QUIZ_SCORE: 'GET_CURRENT_QUIZ_SCORE',
	GET_MAX_ATTEMPTS: 'GET_MAX_ATTEMPTS',
	GET_QUIZ_ATTEMPTS_COUNT: 'GET_QUIZ_ATTEMPTS_COUNT',
	GET_CURRENT_QUIZ_ATTEMPT: 'GET_CURRENT_QUIZ_ATTEMPT'
}

export default {
	[QuizGetters.IS_QUIZ_LOADED]: (state: QuizState): boolean => {
		return !!state && !!state.uuid;
	},
	[QuizGetters.IS_QUIZ_ATTEMPT_FAILED]: (state: QuizState): boolean => {		
		if (!state) {
			return false;
		}

		return skillAssessmentStatusesUtils.isQuizFailed(state);
	},
	[QuizGetters.IS_QUIZ_PASSED]: (state: QuizState): boolean => {
		if (!state) {
			return false;
		}

		return skillAssessmentStatusesUtils.isQuizPassed(state);
	},
	[QuizGetters.IS_FIRST_TIME_QUIZ_NOT_STARTED]: (state: QuizState): boolean => {
		if (!state) {
			return false;
		}

		return skillAssessmentStatusesUtils.isNotStarted(state);
	},
	[QuizGetters.IS_HAVE_RETRY]: (state: QuizState): boolean => {
		if (!state) {
			return false;
		}

		return skillAssessmentStatusesUtils.isFailedAndHaveRetry(state);
	},
	[QuizGetters.IS_QUIZ_IN_PROGRESS]: (state: QuizState): boolean => {
		if (!state) {
			return false;
		}

		return skillAssessmentStatusesUtils.isInProgress(state);
	},
	[QuizGetters.GET_CURRENT_QUESTION]: (state: QuizState): Question | null => {
		if (!state) {
			return null;
		}

		const currentQuiz = skillAssessmentUtils.getAttemptCurrentQuiz(state.quizes);
		if (!currentQuiz) {
			return null;
		}

		return skillAssessmentUtils.getCurrentQuizQuestion(currentQuiz) || null;
	},
	[QuizGetters.GET_QUESTION_BY_UUID]: (state: QuizState): ((uuid: string) => Question | null) => {
		return (uuid: string) : Question | null => {
			if (!state) {
				return null;
			}

			const currentQuiz = skillAssessmentUtils.getAttemptCurrentQuiz(state.quizes);
			if (!currentQuiz) {
				return null;
			}
			
			return currentQuiz.questions.find(q => q.uuid === uuid) || null;
		}
	},
	[QuizGetters.GET_QUIZ_NAME]: (state: QuizState): string => {
		return state?.quiz_metadata.name || 'N/A';
	},
	[QuizGetters.GET_QUESTIONS_COUNT]: (state: QuizState): number => {
		return state?.quiz_metadata.question_per_quiz || 0;
	},
	[QuizGetters.GET_QUIZ_IMAGE]: (state: QuizState): string => {
		return state?.quiz_metadata.image || '';
	},
	[QuizGetters.GET_QUIZ_TIME]: (state: QuizState): number => {
		return state?.quiz_metadata.quiz_duration_minutes || 0;
	},
	[QuizGetters.GET_QUIZ_PASSING_SCORE]: (state: QuizState): number => {
		return state?.quiz_metadata.minimum_passing_score || 0;
	},
	[QuizGetters.GET_QUIZ_DESCRIPTION]: (state: QuizState): string => {
		return state?.quiz_metadata.description || '';
	},
	[QuizGetters.GET_TIME_LEFT]: (state: QuizState): number => {
		if (!state) {
			return 0;
		}

		const currentQuiz = skillAssessmentUtils.getAttemptCurrentQuiz(state.quizes);
		if (!currentQuiz) {
			return 0;
		}
		const current_time = currentQuiz.current_time || 0;
		const start_time = currentQuiz.start_time || 0
		
		const quizDuration = state.quiz_metadata.quiz_duration_minutes * 60 * 1000;
		return quizDuration - (current_time - start_time);
	},
	[QuizGetters.GET_CORRECT_ANSWERS_COUNT]: (state: QuizState): number => {
		if (!state) {
			return 0;
		}

		const currentQuiz = skillAssessmentUtils.getAttemptCurrentQuiz(state.quizes);
		if (!currentQuiz) {
			return 0;
		}

		return currentQuiz.questions.filter(q => (q as AnsweredQuestion).score).length;
	},
	[QuizGetters.GET_CURRENT_QUIZ_SCORE]: (state: QuizState): number => {
		if (!state) {
			return 0;
		}

		const currentQuiz = skillAssessmentUtils.getAttemptCurrentQuiz(state.quizes);
		if (!currentQuiz) {
			return 0;
		}

		return parseInt((skillAssessmentUtils.calculateQuizScore(currentQuiz) || 0).toFixed(0))
	},
	[QuizGetters.GET_MAX_ATTEMPTS]: (state: QuizState): number => {
		if (!state) {
			return 0;
		}

		return state.quiz_metadata.max_attempts;
	},
	[QuizGetters.GET_QUIZ_ATTEMPTS_COUNT]: (state: QuizState): number => {
		if (!state) {
			return 0;
		}

		return state.quizes.length;
	},
	[QuizGetters.GET_CURRENT_QUIZ_ATTEMPT]: (state: QuizState): Quiz | null => {
		if (!state) {
			return null;
		}
		
		return skillAssessmentUtils.getAttemptCurrentQuiz(state.quizes);
	}
}
